import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { getWebSiteSchema, getWebPageSchema, getBreadcrumbSchema } from '@/utils/schemas';

const categories = [
  {
    id: 'cuiguan',
    name: '翠冠梨',
    tagline: '盛夏第一口清甜',
    description: '翠冠梨是惠美梨的明星品种，果实呈圆形或长圆形，果皮黄绿色，果肉洁白细腻，汁多味甜，石细胞极少。每年7月中下旬成熟，是夏季消暑解渴的绝佳选择。',
    features: ['皮薄汁多', '细腻无渣', '清甜爽口', '夏季专属'],
    season: '7月中下旬',
    image: 'https://readdy.ai/api/search-image?query=A%20single%20ripe%20greenish%20golden%20asian%20pear%20with%20smooth%20skin%20on%20a%20minimal%20cream%20background%2C%20soft%20natural%20lighting%2C%20elegant%20food%20photography%2C%20dew%20drops%2C%20clean%20composition%2C%20warm%20natural%20tones%2C%20professional%20product%20photography%2C%20high%20detail&width=600&height=700&seq=cuiguan-pear&orientation=portrait',
  },
  {
    id: 'huanghua',
    name: '黄花梨',
    tagline: '金黄饱满，香甜浓郁',
    description: '黄花梨果实硕大，果皮金黄色，外观极为诱人。果肉酥脆爽口，甜度高、香气浓郁，是梨中佳品。成熟期在8月下旬至9月上旬，正值金秋时节，是中秋送礼的上佳之选。',
    features: ['金黄外观', '酥脆口感', '香甜浓郁', '送礼首选'],
    season: '8月下旬-9月上旬',
    image: 'https://readdy.ai/api/search-image?query=A%20single%20ripe%20golden%20yellow%20asian%20pear%20with%20smooth%20skin%20on%20a%20minimal%20cream%20background%2C%20soft%20natural%20lighting%2C%20elegant%20food%20photography%2C%20warm%20golden%20tones%2C%20clean%20composition%2C%20professional%20product%20photography%2C%20high%20detail&width=600&height=700&seq=huanghua-pear&orientation=portrait',
  },
  {
    id: 'fengshui',
    name: '丰水梨',
    tagline: '汁水丰盈，润泽心田',
    description: '丰水梨果形端正，果皮棕褐色，果肉晶莹剔透、口感极嫩。汁水含量在梨果品种中首屈一指，一口咬下，清甜的梨汁瞬间充盈口腔，令人回味无穷。',
    features: ['汁多如泉', '肉质细嫩', '甜而不腻', '营养丰富'],
    season: '9月上旬-9月下旬',
    image: 'https://readdy.ai/api/search-image?query=A%20single%20ripe%20brown%20skinned%20asian%20pear%20with%20smooth%20texture%20on%20a%20minimal%20cream%20background%2C%20soft%20natural%20lighting%2C%20elegant%20food%20photography%2C%20juicy%20appearance%2C%20clean%20composition%2C%20warm%20earthy%20tones%2C%20professional%20product%20photography&width=600&height=700&seq=fengshui-pear&orientation=portrait',
  },
  {
    id: 'xuehua',
    name: '雪花梨',
    tagline: '洁白如雪，清雅甘甜',
    description: '雪花梨因肉质洁白如雪而得名，果实呈卵圆形，果皮绿黄色。雪花梨口感清雅甘甜，不腻不燥，且耐储存，是惠美梨冬季主推的明星品种。',
    features: ['肉质洁白', '清雅甘甜', '耐储存', '养生佳品'],
    season: '10月上旬',
    image: 'https://readdy.ai/api/search-image?query=A%20single%20ripe%20pale%20green%20asian%20pear%20with%20smooth%20skin%20on%20a%20minimal%20cream%20background%2C%20soft%20natural%20lighting%2C%20elegant%20food%20photography%2C%20white%20flesh%20visible%2C%20clean%20composition%2C%20fresh%20crisp%20aesthetic%2C%20professional%20product%20photography&width=600&height=700&seq=xuehua-pear&orientation=portrait',
  },
];

export default function CategoryPage() {
  const [activeTab, setActiveTab] = useState('cuiguan');
  const activeCategory = categories.find((c) => c.id === activeTab) || categories[0];

  const pageTitle = '惠美梨品种品类 - 翠冠梨、黄花梨、丰水梨、雪花梨 | 惠美梨产地直供';
  const pageDesc = '惠美梨四大核心品种一览：翠冠梨皮薄汁多，黄花梨香甜浓郁，丰水梨汁水丰盈，雪花梨清雅甘甜。覆盖夏秋冬三季，满足不同场景品质梨果需求。了解各品种特色、成熟期与购买方式。';

  const schemas = [
    getWebSiteSchema(),
    getWebPageSchema('惠美梨品种品类', pageDesc, 'https://hwsxsp.cn/category'),
    getBreadcrumbSchema([
      { name: '首页', url: 'https://hwsxsp.cn/' },
      { name: '品种品类', url: 'https://hwsxsp.cn/category' },
    ]),
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        canonicalUrl="https://hwsxsp.cn/category"
        schema={schemas}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">品种品类</span>
            </nav>

            <div className="text-center mb-10 md:mb-14">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
                惠美梨品种品类
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                四大核心品种，覆盖夏秋冬三季，满足不同时节、不同场景的品质梨果需求
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeTab === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex flex-col lg:flex-row gap-10 md:gap-14 items-start" data-product-shop>
              <div className="w-full lg:w-1/2">
                <div className="relative h-80 md:h-[450px] rounded-2xl overflow-hidden">
                  <img
                    src={activeCategory.image}
                    alt={`惠美梨${activeCategory.name}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="w-full lg:w-1/2">
                <div className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                  {activeCategory.tagline}
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  {activeCategory.name}
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-6">
                  {activeCategory.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {activeCategory.features.map((f) => (
                    <span
                      key={f}
                      className="px-3 py-1.5 rounded-lg bg-secondary-100 text-secondary-700 text-sm font-medium"
                    >
                      {f}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-2 text-sm text-foreground-500 mb-8">
                  <i className="ri-calendar-line text-primary-500"></i>
                  <span>最佳赏味期：<strong className="text-foreground-800">{activeCategory.season}</strong></span>
                </div>

                <Link
                  to={`/category/${activeCategory.id}`}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看详细规格
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-12">
              全部品种一览
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  onClick={() => setActiveTab(cat.id)}
                  className="group rounded-2xl bg-background-50 overflow-hidden cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={cat.image}
                      alt={`惠美梨${cat.name}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-foreground-500">{cat.tagline}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}